import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { Receipt } from "@mui/icons-material";
import { ProgramEnrollment } from "../../schemas/programs";
import { formatDateForProgram } from "../../utils/DateUtils";
import { isAdultEnrollment } from "../../utils/ageGroupUtils";
import { getEnrollmentReceiptUrl } from "../../apis/programsApi";

const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case "success":
    case "confirmed":
    case "paid":
    case "completed":
      return "success";
    case "pending":
      return "warning";
    case "pay_at_counter":
      return "warning";
    case "cancelled":
    case "failed":
      return "error";
    default:
      return "default";
  }
};

const isPaidEnrollment = (status?: string) => {
  const normalized = status?.toLowerCase() || "";
  return ["success", "confirmed", "paid", "completed"].includes(normalized);
};

interface EnrollmentDetailsContentProps {
  enrollment: ProgramEnrollment;
}

export const EnrollmentDetailsContent = ({ enrollment }: EnrollmentDetailsContentProps) => {
  const [receiptUrl, setReceiptUrl] = useState(enrollment.receiptUrl || "");
  const [loadingReceipt, setLoadingReceipt] = useState(false);
  const [receiptError, setReceiptError] = useState<string | null>(null);

  const programPrice = Number(enrollment.session?.program?.price || 0);
  const totalAmount = Number(enrollment.totalAmount || 0);
  const showReceipt = isPaidEnrollment(enrollment.paymentStatus);

  const handleViewReceipt = async () => {
    if (receiptUrl) {
      window.open(receiptUrl, "_blank", "noopener,noreferrer");
      return;
    }

    if (!enrollment.id) return;

    setLoadingReceipt(true);
    setReceiptError(null);
    try {
      const url = await getEnrollmentReceiptUrl(enrollment.id);
      setReceiptUrl(url);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setReceiptError("Receipt is not available yet.");
    } finally {
      setLoadingReceipt(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
          Program Information
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Program: {enrollment.session?.program?.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Session: {enrollment.session?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Location: {enrollment.session?.program?.location}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Price: ${programPrice.toFixed(2)}
          </Typography>
        </Box>

        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
          Participant Details
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Name: {enrollment.participantName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Age Group: {enrollment.participantAge}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start Date: {formatDateForProgram(enrollment.selectedStartDate || "")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            End Date: {formatDateForProgram(enrollment.selectedEndDate || "")}
          </Typography>
          {enrollment.timeSlot && (
            <Typography variant="body2" color="text.secondary">
              Time Slot: {enrollment.timeSlot}
            </Typography>
          )}
        </Box>
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
          Contact Information
        </Typography>
        <Box sx={{ mb: 2 }}>
          {isAdultEnrollment(enrollment) ? (
            <Typography variant="body2" color="text.secondary">
              Contact: {enrollment.participantContact || "-"}
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                Parent/Guardian: {enrollment.parentGuardianName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Contact: {enrollment.parentGuardianContact}
              </Typography>
            </>
          )}
          <Typography variant="body2" color="text.secondary">
            Email: {enrollment.user?.email || "-"}
          </Typography>
          {!isAdultEnrollment(enrollment) && (
            <>
              <Typography variant="body2" color="text.secondary">
                Emergency Contact: {enrollment.emergencyContact}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Address: {enrollment.address}
              </Typography>
            </>
          )}
          {enrollment.medicalConditions && (
            <Typography variant="body2" color="text.secondary">
              Medical Conditions: {enrollment.medicalConditions}
            </Typography>
          )}
        </Box>

        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
          Payment Status
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Chip
            label={enrollment.paymentStatus}
            color={getStatusColor(enrollment.paymentStatus) as "success" | "warning" | "error" | "secondary" | "default"}
            sx={{ mb: 1 }}
          />
          {enrollment.totalAmount != null && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Amount Paid: ${totalAmount.toFixed(2)}
            </Typography>
          )}
          {showReceipt && (
            <Button
              variant="outlined"
              size="small"
              startIcon={loadingReceipt ? <CircularProgress size={14} /> : <Receipt />}
              onClick={handleViewReceipt}
              disabled={loadingReceipt}
              sx={{ mt: 0.5, textTransform: "none" }}
            >
              View Receipt
            </Button>
          )}
          {receiptError && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
              {receiptError}
            </Typography>
          )}
        </Box>

        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
          Pricing Breakdown
        </Typography>
        <Box
          sx={{
            mb: 2,
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Program Price:
            </Typography>
            <Typography variant="body2">${programPrice.toFixed(2)}</Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2" fontWeight={700}>
              Total Amount:
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              ${totalAmount.toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};
